import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FORALogomark } from '../components/ui/fora-logomark';
import { OTPInput } from '../components/ui/otp-input';
import { CopperButton } from '../components/ui/copper-button';
import { ArrowRight, Check, ShieldCheck } from 'lucide-react';

const CLINIC_IMAGE = 'https://customer-assets.emergentagent.com/job_clinic-hub-237/artifacts/5frbwspm_danielcubas-waiting-room-548136.jpg';

export const LoginEnhanced = () => {
  const [stage, setStage] = useState(1);
  const [phone, setPhone] = useState('98765 43210');
  const [otp, setOTP] = useState('000000');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(23);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showWordmark, setShowWordmark] = useState(false);
  const imageRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWordmark(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (stage === 2 && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [stage, countdown]);

  // Image is intentionally static — no parallax, no breathing.

  const handleContinue = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLoading(false);
    setStage(2);
    setCountdown(23);
  };

  const handleVerify = async () => {
    setLoading(true);
    const result = await login('receptionist@fora.com', 'receptionist123');
    
    if (result.success) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      navigate('/personas');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-enhanced-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#1A1410]">
        <div
          ref={imageRef}
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${CLINIC_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        {/* Black transparency layer — graduated to keep clinic warmth visible while making white branding pop */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(15, 13, 12, 0.65) 0%, rgba(15, 13, 12, 0.35) 35%, rgba(15, 13, 12, 0.55) 75%, rgba(15, 13, 12, 0.80) 100%)'
          }}
        />
        {/* Soft copper light pool top-left + slate at bottom-right for brand tint */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 25% 15%, rgba(212, 120, 74, 0.28) 0%, transparent 55%), radial-gradient(ellipse at 75% 85%, rgba(61, 90, 128, 0.20) 0%, transparent 60%)'
          }}
        />

        {/* Top-left FORA wordmark */}
        <div className="absolute top-9 left-9 z-10">
          <span
            className="font-['Sora'] text-2xl font-bold tracking-[0.20em]"
            style={{ color: '#FFFFFF', textShadow: '0 2px 14px rgba(0,0,0,0.40)' }}
          >
            FORA
          </span>
        </div>

        {/* Hero block — large FORA wordmark + tagline + signature line, centered */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-12 text-center">
          <h1
            className="font-['Sora'] font-bold mb-5"
            style={{
              color: '#FFFFFF',
              fontSize: 'clamp(64px, 8vw, 112px)',
              letterSpacing: '0.10em',
              lineHeight: 1,
              textShadow: '0 4px 24px rgba(0,0,0,0.50), 0 1px 0 rgba(255,255,255,0.10)'
            }}
          >
            FORA
          </h1>
          <div
            className="h-px w-24 mb-5"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)'
            }}
          />
          <p
            className="font-['Sora'] font-medium tracking-[0.30em]"
            style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '13px',
              textShadow: '0 2px 12px rgba(0,0,0,0.50)'
            }}
          >
            THE OPERATING SYSTEM FOR MODERN CLINICS
          </p>
        </div>

        {/* Bottom-left footer line */}
        <div
          className="absolute bottom-8 left-9 z-10 font-['Inter']"
          style={{
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.55)',
            textShadow: '0 1px 8px rgba(0,0,0,0.50)'
          }}
        >
          Built in India · Made for clinics
        </div>
      </div>

      <div 
        className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12"
        style={{ backgroundColor: 'var(--color-cream)' }}
      >
        <div className="w-full max-w-[380px]">
          <div className="flex flex-col items-center mb-10">
            <FORALogomark size="md" animated />
            <div 
              className={`mt-3 font-['Sora'] text-xl font-bold tracking-[0.10em] transition-all duration-200 ${
                showWordmark ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
              }`}
              style={{ color: 'var(--color-text-primary)' }}
            >
              FORA
            </div>
          </div>

          {stage === 1 && (
            <div className="animate-slide-up" data-testid="phone-stage">
              <div className="mb-8">
                <h1 
                  className="font-['Sora'] text-[26px] font-bold mb-1.5"
                  style={{ color: '#1C1917' }}
                >
                  Welcome back
                </h1>
                <p 
                  className="font-['Inter'] text-sm"
                  style={{ color: '#44403C' }}
                >
                  Sign in to continue
                </p>
              </div>

              <div className="mb-6">
                <label 
                  className="text-overline block mb-2"
                  style={{ color: '#78716C' }}
                >
                  PHONE NUMBER
                </label>
                <div 
                  className="glass-subtle rounded-[var(--radius-md)] h-[52px] flex items-center px-4 transition-all duration-180 focus-within:bg-[rgba(255,255,255,0.65)] focus-within:ring-4 focus-within:ring-[rgba(61,90,128,0.08)]"
                >
                  <span 
                    className="font-['DM_Mono'] text-sm"
                    style={{ color: '#78716C' }}
                  >
                    +91
                  </span>
                  <div 
                    className="w-px h-6 mx-3"
                    style={{ backgroundColor: 'rgba(28, 25, 23, 0.12)' }}
                  />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none font-['Inter'] text-base"
                    style={{ 
                      color: '#1C1917',
                      letterSpacing: '0.02em'
                    }}
                    placeholder="98765 43210"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <CopperButton
                size="lg"
                className="w-full mb-5"
                onClick={handleContinue}
                disabled={loading}
                data-testid="continue-button"
              >
                {loading ? (
                  <span className="animate-pulse">Processing...</span>
                ) : (
                  <>
                    Continue
                    <ArrowRight size={16} strokeWidth={1.75} />
                  </>
                )}
              </CopperButton>

              <div className="flex items-center justify-between">
                <button 
                  className="font-['Inter'] text-[13px] transition-colors duration-180 hover:text-[#1C1917]"
                  style={{ color: '#78716C' }}
                >
                  Need help?
                </button>
                <button 
                  className="font-['Sora'] text-[13px] font-semibold transition-colors duration-180 hover:text-[#8E4A23]"
                  style={{ color: '#B5612E' }}
                >
                  Start free →
                </button>
              </div>
            </div>
          )}

          {stage === 2 && (
            <div className="animate-slide-up" data-testid="otp-stage">
              <div className="mb-8">
                <h1 
                  className="font-['Sora'] text-[26px] font-bold mb-1.5"
                  style={{ color: '#1C1917' }}
                >
                  Enter verification code
                </h1>
                <p 
                  className="font-['Inter'] text-sm"
                  style={{ color: '#44403C' }}
                >
                  We sent a code to +91 {phone}
                </p>
              </div>

              <div className="mb-4">
                <OTPInput
                  length={6}
                  value={otp}
                  onChange={setOTP}
                  onComplete={() => {}}
                />
              </div>

              <div className="flex items-center justify-between mb-6">
                <span 
                  className="font-['Inter'] text-xs"
                  style={{ color: 'var(--color-slate)' }}
                >
                  Resend in 0:{countdown.toString().padStart(2, '0')}
                </span>
                <button 
                  className="font-['Sora'] text-xs font-semibold transition-colors duration-180 hover:text-[var(--color-copper-deeper)]"
                  style={{ color: 'var(--color-copper-dark)' }}
                  onClick={() => setStage(1)}
                >
                  Use different number
                </button>
              </div>

              <CopperButton
                size="lg"
                className="w-full"
                onClick={handleVerify}
                disabled={loading}
                data-testid="verify-button"
              >
                {loading ? (
                  <span className="animate-pulse">Verifying...</span>
                ) : (
                  <>
                    Verify
                    <Check size={16} strokeWidth={1.75} />
                  </>
                )}
              </CopperButton>
            </div>
          )}

          <div className="fixed bottom-8 left-1/2 lg:left-3/4 transform -translate-x-1/2 flex items-center gap-2">
            <ShieldCheck 
              size={12} 
              strokeWidth={1.75}
              style={{ color: 'var(--color-slate)', opacity: 0.7 }}
            />
            <span 
              className="font-['Inter'] text-[11px]"
              style={{ 
                color: '#78716C',
                letterSpacing: '0.04em'
              }}
            >
              ABDM (Ayushman Bharat Digital Mission) Ready
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginEnhanced;
